import { CollectionWithBingoType } from 'src/domains/casino/types/types';
import { RightHandSideViewType } from 'src/domains/layouts/state/router/newRouter/rhsRouteType';
import { ConfigFeaturesType } from 'src/domains/common/configFeatures/ConfigFeatures';

// GROWTH TYPES
export type HeaderLinksType =
    | 'sports'
    | 'in-play'
    | 'mcbookie-blog'
    | 'star-news'
    | 'dragonbet-news'
    | CollectionWithBingoType
    | HeaderSportsLinksType;

export type HeaderSportsLinksType = 'football' | 'horse racing';

export interface LanguageDetailsType {
    langCode: string;
    langName: string;
}

export enum DefaultComponentVariantType {
    PRIMARY = 'primary',
    SECONDARY = 'secondary',
}

export enum ButtonVariantType {
    PRIMARY = 'primary',
    SECONDARY = 'secondary',
}

export enum TooltipVariantType {
    PRIMARY = 'primary',
    SECONDARY = 'secondary',
}

export enum SwitchVariantType {
    LIGHT = 'light',
    DARK = 'dark',
}

export enum AccountNavVariantType {
    PRIMARY = 'primary',
    SECONDARY = 'secondary',
}

export enum NavMenuVariantType {
    PRIMARY = 'primary',
    SECONDARY = 'secondary',
}

export enum NavigationVariantType {
    PRIMARY = 'primary',
    SECONDARY = 'secondary',
}

export enum InputVariantType {
    PRIMARY = 'primary',
    SECONDARY = 'secondary',
}

export type ComponentVariantsType = {
    // Secondary button it's with rounded corners
    buttonVersion: ButtonVariantType;
    tooltipVersion: TooltipVariantType;
    inputVariant: InputVariantType;
    nextOffSwitchVariant: SwitchVariantType;
    miniGamesVariant: DefaultComponentVariantType | null;
    accountNavVariant: AccountNavVariantType | null;
    casinoVariant: DefaultComponentVariantType | null;
    navMenuVariant: NavMenuVariantType | null;
    navigationVariantType: NavigationVariantType | null;
    singleScoreVariant: DefaultComponentVariantType;
    traderChatVariant: DefaultComponentVariantType | null;
    nudgeVariant: DefaultComponentVariantType | null;
    systemPagesVariant: DefaultComponentVariantType | null;
    filtersVariantType: DefaultComponentVariantType | null;
    betslipVariantType: DefaultComponentVariantType | null;
    modalVariantType: DefaultComponentVariantType | null;
    raceCardVariant: DefaultComponentVariantType | null;
    selectionVariant: DefaultComponentVariantType | null;
    sportsbookVariant: DefaultComponentVariantType | null;
    scoreVariant: DefaultComponentVariantType | null;
    collapseVariant: DefaultComponentVariantType | null;
    accountSectionVariant: DefaultComponentVariantType | null;
    selectVariant: DefaultComponentVariantType | null;
    quickTrayLinkVariant: DefaultComponentVariantType | null;
};

export type SignupType = 'primary' | 'secondary';

export type MobileFooterLinksType =
    | 'cash-out'
    | 'football-european'
    | 'football'
    | 'home'
    | 'in-play'
    | 'offers'
    | 'promos'
    | 'racing'
    | 'sports-list'
    | 'sportsbook'
    | 'extra'
    | 'ak-big-prices'
    | 'ak-rewards'
    | Exclude<CollectionWithBingoType, 'virtuals'>;

export type UsefulLinksKeysType =
    | 'about-us'
    | 'responsible-gambling'
    | 'safer-gambling'
    | 'faq'
    | 'privacy-policy'
    | 'terms-and-conditions'
    | 'cookie-policy'
    | 'betting-rules'
    | 'give-feedback'
    | 'affiliates'
    | 'careers';

export type FooterUsefulLinksItemType = {
    id: UsefulLinksKeysType;
    order: number;
    to: RightHandSideViewType | string;
    target?: '_blank' | '_self' | '_parent' | '_top' | null;
    route?: string;
    isStaticLink?: boolean;
};

// PLAYERS TYPES

export type PaysafePaymentProviderType = {
    type: 'paysafe';
    apiKey: string;
    environment: 'TEST' | 'LIVE';
    cardAccounts: { GBP: string; EUR: string };
    applePayEnabled: boolean;
};

type PaymentProvidersType = { type: 'realex' } | { type: 'praxis' } | PaysafePaymentProviderType | { type: null };

export type MultiDepositPaymentProviderType = {
    debitCard: PaymentProvidersType;
    openBanking: { type: 'yaspa'; isEuroAllowed: boolean; allowOnlyWithdraw: boolean } | null;
    applePay: PaysafePaymentProviderType | null;
    isNewPaymentJourneyEnabled: boolean;
};

export type ContactPreferencesType = 'email' | 'sms' | 'phone' | 'whatsApp';

// SPORTSBOOK TYPES
export type ListOfSportsType =
    | 'americanfootball'
    | 'australianrules'
    | 'badminton'
    | 'baseball'
    | 'basketball'
    | 'boxing'
    | 'cricket'
    | 'cycling'
    | 'darts'
    | 'esports'
    | 'football'
    | 'formulaone'
    | 'gaelicfootball'
    | 'gaelichurling'
    | 'golf'
    | 'greyhoundracing'
    | 'handball'
    | 'horseracing'
    | 'horseracingantepost'
    | 'icehockey'
    | 'internationalhorseracing'
    | 'mixedmartialarts'
    | 'motorsport'
    | 'olympics'
    | 'politics'
    | 'rugbyleague'
    | 'rugbyunion'
    | 'snooker'
    | 'tabletennis'
    | 'tennis'
    | 'tennisdoubles'
    | 'tvspecials'
    | 'volleyball';

// the first item in an array is a default language for the operator
export type LanguagesAvailableType = Array<LanguageDetailsType>; // doppler

export type ConfigType = ConfigFeaturesType;

export type ConfigKeysType = keyof ConfigType;

export const configTypesDesc: Record<keyof ConfigType, string> = {
    // CASINO CONFIG
    /* A */
    /* B */
    bonusEngine: 'accounts bonuse engine',

    /* C */
    casinoJackPot: 'set casino jackpot games for each operator',
    casinoMiniGames: 'contact possibilities for Contact Us section in the main footer',
    casinoStore: 'switch for games source between casino-store and website-cms',
    /* G */
    gameStatusBar: 'turn on/off game status bar for slots',
    gameThumbnailSettings: 'defines what features should be displayed on SingleGameExt',
    /* H */
    hasContinuePlayingName: 'turn on/off users name in continue playing headline', // false only for LK but on designs it should be there - fix and remove
    hasHeadlinePromo: 'turn on/off headline carousel view',
    hasGamesOnHomepage: 'turn on/off games on homepage',
    hideAllCasinoGamesOnChristmas: 'show / hide all casino game on Christmas day.',
    homepageWidgetsOrder: 'order of widgets on homepage',
    /* I */
    imageOptimization: 'turn on/of image optimization with Cloudflare',
    /* R */
    reloadGameAfterDepositForProviders: 'list of providers that require reload game after deposit',
    /* S */
    socketPragmatic: 'DGA for live casino',

    // GROWTH CONFIG
    /* A */
    accountDrawerNew: 'New account drawer',
    appDownloadLink: 'Links for mobile app in store',
    applePayHiddenForEuro: 'Hide apple pay for euro',
    /* C */
    cookieBotId: "Operator's cookiebot cookie consent banner id.",
    componentVariants: 'Custom components of the layout',
    contactDetails: 'contact possibilities for Contact Us section in the main footer', // used only for LK email
    contactUsTemporaryDefaultMock: 'temporary contact us list',
    /* F */
    filtersVersion: 'set proper version for all filters across the website.',
    footerCopyrightTemporaryDefaultMock: 'temporary footer copyright',
    footerUsefulLinks: 'Useful links section in footer',
    footerSaferGamblingLogos: 'Responsible gambling logos in footer',
    freshDeskChat: 'Fresh Desk chat',
    fullstory: 'fullstory tool to track user behaviour on website ',
    /* G */
    gtm: 'google tag to google tag manager - prod and non prod',
    googleExperimentIdDev: 'google optimize experiment id for dev',
    googleExperimentIdProd: 'google optimize experiment id for production',
    /* H */
    sendInfoAboutSignUpToMobileApp: 'has to collect info about signup on mobile app',
    hasCarouselPromo: 'turn on/off promo carousel view',
    hasFooterCopyrightTCLink: 'link terms & conditions in footer copyright text',
    headerLinks: 'header links for universes',
    headerMetaScale: '',
    headerMetaTitleName: '',
    headerMetaFacebookDomainVerification: 'Unique code for facebook domain verification meta tag',
    headerSpecialLink: "link to the operator's blog / news section",
    heroWidgetViewRacecard: 'turn on/off View Racecard button on Hero Widget',
    hiddenSportsList: 'sports which have to be hidden',
    hideEmptySports: 'flag to hide sports with no events on A-Z sports list',
    hideAllSportsFilterOnHomepageInPlaySection: 'Hide All Sports filter on Homepage in section In Play',
    /* I */
    idMission: 'enable idMission',
    isDarkUniverse: 'add icon ios in headerMeta',
    isNoConnectionPageVisible: 'Show no connection page', // all true
    isBoostedPriceAvailable: 'Show boosted price on selection',
    isSeoPagesContentOn: 'turn on/off seo pages content',
    isRealityCheckAvailable: 'enable Reality check popup', // all true
    isRasGoldActive: 'Show RAS Gold data on horse racing',
    /* L */
    languagesAvailable: 'languages available for an operator', // doppler
    limitsAndResponsibleGambling: 'keeps routes and components of Limits & Responsible Gambling', // all true but doppler
    /* M */
    metaOpenGraph: 'apply meta open graph in html header',
    mobileFooterLinks: 'mobile footer links for all universes',
    /* N */
    navigationPopular: 'sports to show in popular section',
    navigationFavourites: 'show favourites section',
    navigationRecentlyViewed: 'show recently viewed section',
    navigationMobileLinks:
        'set mobile navigation links different to the top nav/header ones or the same like top nav/header ones',
    isSaferGambling: 'turn on new gambling commission regulations features and urls',
    notificationsList: 'switch to render notifications', // all true
    /* O */
    operatorFullName: 'Full name of each operator',
    outsideAllowedRoutes:
        'Routes from external server with the same domain which are used for promotion, news, blogs etc', // all the same
    optimizeHRCarousel: 'optimized HR carousel enabled',
    onlyCountriesForSignUp: 'only countries for signup dropdown',
    /* P */
    page404Default: 'add additional elements and styles when 404 error page is not a default one',
    paymentProviders: 'Payment providers configuration',
    problemGambling: 'Show Problem Gambling link in responsible gambling section',
    promotedEvents: "Show promoted events in LHS menu and main page and set route'accountHelperMail",
    /* R */
    /* S */
    seoCanonical: 'seo canonical',
    seoMeta: 'seo title, description',
    seoMetaSport: 'seo headers, title, description for sport',
    seoMetaSpecialSports: 'seo headers, title, description for special sports',
    scoreboard:
        'set scoreboard version: basic (only score and time), standard (old scoreboard with animation), liveTracker (new live tracker with animation), lSport (LSport tennis scoreboard), imgArena for golf',
    sentryUrl: 'Sentry error tracker url', // all the same
    siteVerificationMetaMap: 'header meta css',
    statsCoreGroupId: 'Set groupId from Statscore to see pre-match widget and live match widget for tennis and golf.', // all the same
    socialWidgets: 'configuration social widget on RHS menu',
    /* T */
    termsAndConditionsDownloadLink: 'option to download T&C',
    traderChat: 'turn on/off trader chat',
    topEvents: 'Show top events on homepage instead of promoted events',

    /* T */
    unSupportedBrowsers: 'turn on component for unSupportedBrowsers',
    unSupportedBrowsersLogo: 'set logo for unSupportedBrowsers component',

    // PLAYERS CONFIG
    /* A */
    accountHelperMail: 'account for help contact',
    accountIdPrefix: 'prefix for account Id',

    /* C */
    casinoPublishedCollections: 'defines published casino collections',
    cmsPromoCodes: 'show promo codes from CMS not Bonus Engine',
    complianceNew: 'enable new compliance service',
    contactPreferences: 'defines contact preferences options',
    contactPreferencesCategories: 'defines contact preferences categories',
    countryOnlyUKDefault: 'only default country in signup', // doppler
    countrySignUpDefault: 'default country in sign up',
    currencyDefault: 'default currency',
    currencyDefaultSelectedInSignup: 'have default currency selected in signup',
    currencyOnlyDefault: 'only default currency in signup',
    currencySignUpList: 'list of currencys in signup',
    customerFundsProtection: 'disable customer funds protection tab',
    /* D */
    decimalLength: 'specify how many places after comma we want to have in value',
    decimalFormatTextForPlaceholder: 'specify how we will display stake input placeholder text',
    detectLoginInMobileApp: 'detect login after signup in mobile app',
    defaultPromoCodeFieldOpen: 'signup default promo code field open',
    depositLimitsType: 'ui change for picking limits based on calendar or time window (rolling)',
    depositsTurnOffForLeavingOperator: 'turn off deposits for the leaving operator',
    datadogLogsEnabled: 'turn on datadog logs',
    /* E */
    emailKycWarning: 'email to kyc warning',
    /* F */
    forgotPasswordInputLabel: 'input label type in forgot password form',
    forgotPasswordInputType: 'input type in forgot password form `email` | `text`',
    failedVerificationDetailed: '@description detailed failed verification page in sign up journey',
    faqSection: 'turn on/off faq section for authorized nad non authorized user.',
    /* H */
    hasCashoutEnabled: 'turn on/off able to cashout', // all true
    hasNeedHelpAccountSection: 'turn on/off section with help in account', // all true
    hasUserAddressChangeEnabled: 'turn on/off able to change address', // all true
    hasUserContactNumberChangeEnabled: 'turn on/off able to change contact number', // all true
    hasUserEmailChangeEnabled: ' turn on/off able to change email address', // all true
    /* I */
    isVerifyAccountWithSMSAvailable: 'enable the account verification via SMS',
    intercomChat: 'enable intercom Chat',
    intercomChatAppId: 'App ID for Intercom Chat - get from intercom panel',
    intercomChatRegion: 'Set region for Intercom Chat',
    intercomChatApiBase: 'URL for Intercom Chat API base',
    identityPhoneContact: 'phone contact for identity verification issues',

    /* L */
    loginOnResetPasswordSuccess: 'autologin after reset password success',
    licenseName: 'license name',
    /* M */
    minDepositAmount: 'minimum deposit amount',
    minWithdrawAmount: 'minimum withdraw amount',
    multibetWidget: 'Multibet widget configuration with base URL and product name',
    betBuilderForInPlay: 'Enable Bet Builder for InPlay events (requires multibetWidget to be configured)',
    /* N */
    navigationHelpSupport: 'show help and support section',
    /* O */
    optimoveWorkerEnabled: 'enable Optimove service worker',
    optimoveInboxCustomised: 'enable customized Optimove inbox styles',
    optInEnabled: 'enable possibility to view and engage with Opt In promos',
    /* P */
    prefixAndFlagDefaultForPersonalDetails: 'Display only one flag and prefix in personal details page', // doppler
    prefixAndPhoneNumberDefault: 'show default prefix for personal details in account',
    prefixMultiPhoneNumber: 'Show dropdown for mobile prefixes in registration and settings', // all true
    priorityPrefixes: 'priority prefixes that are in the front of phone number prefixes list',
    pofModals: 'Show Proof of Funds modals',

    /* R */
    resetPasswordForm: 'reset password form ResetPasswordForm | McBookieValidateAccountForm',
    ringFencedFundsSetByDefault: 'set ringFencedFunds by default during signup',
    rollingNetDepositLimit:
        'turn on rolling deposit limits in signup and responsible gambling tab plus check it when user logs in',
    rollingNetDepositLimitFirstTimePopupType:
        'popup for users after adding new feature to operators - only when feature was created',
    resourceSSR: 'experimental for test resourceSSR',

    /* S */
    showKycStatus: 'show KycStatus',
    showKycDepositWaring: 'show Kyc warning in deposit tab',
    signUpCheckboxTC: 'signup checkbox for Terms & Conditions',
    signUpDisableButtonYourDetails: 'signUp disable button YourDetails', //all false
    signUpFooterMail: '',
    signUpShowPromoCode: '',
    signUpAddressSearchByPostCode: 'turn on/off searching addres by postcode in singup procedure', // doppler
    signUpOmitAgeVerification: 'pass registration without checking age verification',
    signUpSecondAddressLine: 'show second line option to choose during signup',
    signUpTitle: 'show title option to choose during signup',
    signupPrivacyPolicyLink: 'show signup privacy policy link on signup',
    signupTurnOffForLeavingOperator: 'turn off signup for leaving operator',
    signupAdditionalNoteOnLimits: 'has additional note about empty limits',
    srgQuestionnaire: 'show the SRG Questionnaire form modal',
    srgQuestionnaireFailedVersion: 'Show version of SRG Questionnaire that failed verification',
    /* T */
    timeoutToTestSignupFlow: 'use to change customer settings in BO', // dev for all
    /* U */
    userCanChangeRealityCheck: 'user can change Reality Check duration',
    /* Y */
    yaspaMobileAppValidVersion: 'Version from which Yaspa is valid on mobile apps',
    /* Z */
    zipCodeAndCountryCheck: 'Changed name of postcode to zip code and validate signup dropdown field',

    // SPORTSBOOK CONFIG
    /* B */
    buildRacecardCheckboxColorMode: 'set checkbox colors for Build Racecard',

    /* C */
    couponForEnv: 'coupon for environment',
    /* E */
    eventsEuropeanEliteMcbookie: 'get ids europeanEliteTemplate from template mcbookie', // 4 years ago for mcb - check if still needed
    eventsOnLaterHomePage: 'if route is promoted-events show only eventsOnlater in mid layout',
    excludedCountriesFromSignUp: 'Remove specific countries from signup page',
    /* F */
    filterSortingMode: 'Choose sorting mode in filters component. We are sorring by name and startTime',
    /* H */
    hasEventLiveStreaming: 'show icons and buttons for live event stream',
    hasInPlayDelayInformation: 'turn on delay information in InPlayComponent', // all true
    hasTopUpEnabled: 'turn on/off top up in betslip', // all true
    /* I */
    isAntePostReverse: 'add icon ios in headerMeta',
    isAutomaticCarousel: 'should promo carousel be automatic',
    isNextRacesToggleActive: 'determines whether next races region toggle is active by default',
    isStreamControlledByTime: 'decide if stream component on Special Event should be controlled by time', // all false
    isStreamVisibleForUnauthorized: 'decide if stream component should be visible for unauthorized users', // all true
    /* L */
    liveTracker: 'live tracking scoreboard configuration - provider name and script src',
    liveTrackerForFootballOutrights: 'Specifies whether live tracker should be displayed for football outrights',
    /* N */
    noBetslip: 'universe has no betslip',
    /* O */
    oddsFormatShortDefault: 'default odds for anonymous user',
    /* P */
    /* R */
    rab: 'turn on RAB events',
    rabCashoutAvailable: 'decscription in YouDecideHeader (Unavailable for cashout)',
    rabHeaderIcon: 'BetBuilderIcon', // all the same
    rabHeaderTitle: 'title for rab header and bets',
    racingNextOffToggle: 'set switch between HR and GR next off',
    realityCheckLimitedType: 'has less options to pick for reality check',
    /* S */
    showApplePayBanner: 'show apple pay banner on deposit page',
    showHorseRacingSortDropdown: 'show sort dropdown on Horse Racing Racecard',
    shouldPlaceBetToWatchStream: 'decide if user has to place a bet to enable stream',
    sliceNameRaceLabel: 'turn on slice name in race label',
    sportGreyhoundMeetingsSplitByCountry: 'decide if GR sport meetings are splitted by country',
    sportHorseracingMeetingsSplitByCountry: 'decide if HR sport meetings are splitted by country',
    sportsNamesCapitalized: 'make sport names in LHS capitized', // doppler
    startingSoonEvents: 'show starting soon events on homepage',
    /* T */
    trapChallenge: 'enable Trap Challenge',
    /* U */
    universeSetup: '', //sbk
    /* W */
    withdrawDropdownNew: 'determines whether to use new dropdown that displays net deposits in withdrawals',
    withdrawValidateBtcAddress: 'determines whether to validate the wallet address in BTC',
    /* X */
    xtremePush: 'determines whether to use show xtreme push banner on home and casino page',
};

/*
    Prod - Star
host: "dga.pragmaticplaylive.net",
casinoId: "ppcdg00000004198",

    Prod - Mcbookie
host: "dga.pragmaticplaylive.net",
casinoId: "ppcdg00000004197",

    STG - Mcbookie
host: "prelive-dga.pragmaticplaylive.net",
casinoId: "ppcdk00000005025",
*/
